import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { tokenDeAcesso } from '../auth/armazenamento'
import { renovarSessao } from '../auth/renovacao'

/** Corpo de erro da API (RFC 7807 + extensões nossas). */
export interface ProblemDetail {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  codigo?: string
  requestId?: string
  erros?: { campo: string; mensagem: string }[]
  motivos?: string[]
  /** O que falta no plano, quando a transição foi recusada por RDM incompleta. */
  pendencias?: string[]
  transicoesPermitidas?: string[]
}

/** Erro já traduzido para consumo da UI. */
export class ErroDaApi extends Error {
  constructor(
    readonly problema: ProblemDetail,
    readonly status: number,
  ) {
    super(problema.detail ?? problema.title ?? 'Não foi possível concluir a operação.')
    this.name = 'ErroDaApi'
  }

  /** Código estável (ex.: GMUD_TRANSICAO_INVALIDA). A UI reage a isto, nunca ao texto. */
  get codigo(): string {
    return this.problema.codigo ?? 'ERRO_DESCONHECIDO'
  }
}

export const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
  headers: { Accept: 'application/json' },
})

/** Toda chamada leva a sessão. O token é lido a cada requisição, nunca capturado num closure. */
api.interceptors.request.use((config) => {
  const token = tokenDeAcesso()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

type RequisicaoComRetentativa = InternalAxiosRequestConfig & { _renovada?: boolean }

/**
 * Normaliza toda falha num ErroDaApi — e tenta renovar a sessão uma única vez.
 *
 * O componente nunca precisa saber se o backend caiu, se a rede falhou ou se foi
 * regra de negócio — ele sempre recebe a mesma forma, com um código e uma mensagem
 * em português que pode ir direto para a tela.
 *
 * O token de acesso dura 15 minutos. Sem a renovação aqui, quem deixa a tela aberta
 * durante uma reunião do comitê volta e leva "sessão expirada" no meio de uma
 * aprovação. A marca `_renovada` garante uma tentativa só: se a segunda também der
 * 401, a sessão acabou mesmo e insistir viraria laço.
 */
api.interceptors.response.use(
  (res) => res,
  async (erro: AxiosError<ProblemDetail>) => {
    const requisicao = erro.config as RequisicaoComRetentativa | undefined

    if (erro.response?.status === 401 && requisicao && !requisicao._renovada && podeRenovar(requisicao.url)) {
      requisicao._renovada = true
      const sessao = await renovarSessao()
      if (sessao) {
        return api.request(requisicao)
      }
    }

    const status = erro.response?.status ?? 0
    const problema: ProblemDetail = erro.response?.data ?? {
      title: 'Sem conexão com o servidor',
      detail: 'Verifique sua conexão e tente de novo. Se persistir, avise o suporte.',
      codigo: 'SEM_CONEXAO',
    }
    return Promise.reject(new ErroDaApi(problema, status))
  },
)

/** O próprio fluxo de autenticação não se renova: 401 ali significa que a sessão acabou. */
function podeRenovar(url: string | undefined): boolean {
  return !url?.startsWith('/auth/')
}
